import { useState, useEffect } from 'react';
import { Container, TextField, Button, Card, Typography, CircularProgress, Divider, Tooltip, Modal, Box, Tabs, Tab } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { contactUs, fetchSendEmailCode, getVerifyCode } from '../api/api';
import { FaCheckCircle } from 'react-icons/fa';
import { BsShieldFillCheck } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import { FormError } from '../components/FormError';

const ContactUs = () => {
  const [submitStatus, setSubmitStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [codeErrorMessage, setCodeErrorMessage] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [verifyChoose, setVerifyChoose] = useState<string>('email');
  const navigate = useNavigate();

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (!name.trim()) {
      enqueueSnackbar('Please enter your name.', { variant: 'error' });
      return;
    }
    if (!email.trim()) {
      enqueueSnackbar('Please enter a valid email.', { variant: 'error' });
      return;
    }
    if (!company.trim()) {
      enqueueSnackbar('Please enter your company.', { variant: 'error' });
      return;
    }
    if (!message.trim()) {
      enqueueSnackbar('Please enter your message.', { variant: 'error' });
      return;
    }

    setShowVerifyModal(true);
    sendVerificationCode(email);
  };

  const sendVerificationCode = async (email: string) => {
    setSendingCode(true);
    setCodeError(false);
    try {
      await fetchSendEmailCode(email);
      setCountdown(90);
    } catch (err: any) {
      setCodeErrorMessage(err.message || 'Failed to send code.');
      setCodeError(true);
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code || code.length < 6) {
      setCodeErrorMessage('Please enter a valid verification code.');
      setCodeError(true);
      return;
    }

    setVerifying(true);
    try {
      const res = await getVerifyCode('email', email, code);
      if (res?.status === '1') {
        setShowVerifyModal(false);
        submitContactForm();
      } else {
        setCodeErrorMessage('Invalid verification code.');
        setCodeError(true);
      }
    } catch (err: any) {
      setCodeErrorMessage(err.message || 'Verification failed.');
      setCodeError(true);
    } finally {
      setVerifying(false);
    }
  };

  const submitContactForm = () => {
    setLoading(true);
    contactUs(name, email, company, message).then((res) => {
      if (res.status === '1') {
        setSubmitStatus(true);
        enqueueSnackbar('Submit Successfully.', { variant: 'success' });
        navigate(`/`);
      } else {
        enqueueSnackbar('Network error, please try again', { variant: 'error' });
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const chooseChangeHandel = (event: any, newValue: any) => {
    setVerifyChoose(newValue);
  };

  return (
    <div className="container max-w-7xl mx-auto px-4 md:px-8 my-10">
      <Typography variant="h4" className="font-bold">Contact Us</Typography>
      <Typography variant="h6" className="font-light mt-2">
        Our team is happy to answer your questions. Fill out the form and we'll be in touch as soon as possible.
      </Typography>
      {
        <div>
          <div className="mt-8">
            <label className="flex mb-1">Your Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-transparent w-full md:w-2/3 border-b border-black py-4 px-2"
            />
          </div>
          <div className="mt-8">
            <label className="flex mb-1">Email</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent w-full md:w-2/3 border-b border-black py-4 px-2"
            />
          </div>
          <div className="mt-8">
            <label className="flex mb-1">Company</label>
            <input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="bg-transparent w-full md:w-2/3 border-b border-black py-4 px-2"
            />
          </div>
          <div className="mt-8 relative w-full md:w-2/3">
            <label className="flex mb-1">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="bg-transparent w-full border border-black py-2 px-2 pr-10 resize-none"
            />
          </div>
          <div className="w-full flex justify-start mt-5">
            <div className="w-full md:w-2/3 flex justify-center">
              <Button
                onClick={handleSubmit}
                variant="contained"
                className={`bg-gradient-to-b from-cyan-400 to-sky-500 text-white font-medium rounded-md px-5 py-3 flex gap-2 items-center shadow-md hover:from-cyan-500 hover:to-sky-600 ${loading && "cursor-not-allowed"}`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} />
                    Processing...
                  </>
                ) : (
                  <>
                    Submit
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      }

      <Modal
        open={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        aria-labelledby="email-verification-modal"
        aria-describedby="enter-email-verification-code"
      >
        <Box className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white shadow-lg rounded-lg p-2 w-full max-w-lg">
          <div className="flex justify-between mt-1 mb-2 px-4 pt-2">
            <h2 className="text-xl font-semibold text-gray-700">Email Verification</h2>
            <button
              className="inline-block w-8 h-8 mask bg-[url('../public/images/X.svg')] bg-no-repeat bg-center border-none cursor-pointer"
              onClick={() => setShowVerifyModal(false)}
            />
          </div>
          <div className="container mx-auto p-6">
            <p className="text-slate-500 leading-7 mb-2">
              We sent code to <strong>{email}</strong>.
            </p>
          
            <div>
              <div className="mt-4">Two-Step Verification</div>
              <div className="w-full max-w-lg flex">
                <input
                  className="flex-1 bg-transparent border-b border-black py-4 px-2"
                  id="emailVerification"
                  name="emailVerification"
                  placeholder="Enter the email verification code..."
                  type="text"
                  value={code}
                  onChange={(evt) => {
                    if (evt.target.value) {
                      setCodeError(false);
                    }
                    setCode(evt.target.value);
                  }}
                  disabled={verifying}
                  maxLength={6}
                />
                <Button
                  variant="contained"
                  className={`${countdown > 0 ? '' : 'bg-blue-600'} text-white rounded w-40`}
                  disabled={countdown > 0 || sendingCode}
                  onClick={() => sendVerificationCode(email)}
                >
                  {sendingCode ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : countdown > 0 ? (
                    `${countdown}s`
                  ) : (
                    'Resend Code'
                  )}
                </Button>
              </div>
              {codeError && <FormError message={codeErrorMessage} />}
              <div className="flex justify-center h-10 mt-8 mb-4">
                <Button
                  variant="contained"
                  onClick={handleVerifyCode}
                  className={`${verifying ? '' : 'bg-blue-600'} text-white rounded w-full max-w-xs`}
                  disabled={verifying}
                >
                  {verifying ? (
                    <>
                      <CircularProgress size={20} color="inherit" /> Verifying...
                    </>
                  ) : (
                    'Verify & Submit'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default ContactUs;